import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Radio as RadioIcon,
  AccountCircle as AccountIcon,
  History as HistoryIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import iconImage from '../assets/icon.png';
import apiClient from '../utils/apiClient';
import { useToast } from '../contexts/ToastContext.tsx';
import { usePermissions } from '../hooks/usePermissions.tsx';

interface DispatcherHeaderProps {
  iconLoaded: boolean;
  setIconLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  dispatchStatus: string;
  currentTime: Date;
  isFetching: boolean;
  onShowAccountDialog: () => void;
  onShowClosedCalls: () => void;
  onShowShiftCommander: () => void;
  onShowAdminDialog: () => void;
  onShowRadio: () => void;
  onLogout: () => void;
  onReturnToShiftSelection?: () => void;
}

const DispatcherHeader: React.FC<DispatcherHeaderProps> = ({
  iconLoaded,
  setIconLoaded,
  dispatchStatus,
  currentTime,
  isFetching,
  onShowAccountDialog,
  onShowClosedCalls,
  onShowShiftCommander,
  onShowAdminDialog,
  onShowRadio,
  onLogout,
  onReturnToShiftSelection
}) => {
  const { showError } = useToast();
  const { hasPermission } = usePermissions();

  const handleDispatchStatusChange = async (status: string) => {
    try {
      const response = await apiClient.updateUnitStatus('DISPATCH', status);
      
      if (!response.success) {
        console.error('Failed to update dispatch status:', response.error);
        showError('Failed to update dispatch status');
      }
    } catch (error) {
      console.error('Error updating dispatch status:', error);
      showError('Failed to update dispatch status');
    }
  };

  const getStatusColor = (status: string) => {
    // Using exact colors from Config.lua UNIT_COLOR
    switch (status) {
      case 'I/S': return '#32be5a'; // Color3.fromRGB(50,190,90)
      case 'DISP': return '#ffd25a'; // Color3.fromRGB(255,210,90)
      case 'E/R': return '#f0b432'; // Color3.fromRGB(240,180,50)
      case 'O/S': return '#2867c7'; // Color3.fromRGB(40,103,199)
      case 'TRX': return '#9f3e3e'; // Color3.fromRGB(159,62,62)
      case 'OOS': return '#909090'; // Color3.fromRGB(144,144,144)
      default: return '#909090';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }) + ' UTC';
  };


  return (
    <AppBar position="static" sx={{ backgroundColor: '#16181c', height: 60, flexShrink: 0 }}>
      <Toolbar sx={{ minHeight: 60, height: 60, justifyContent: 'space-between' }}>
        {/* Left side: Icon, Title and Status buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={iconImage}
            alt="Firestone CAD"
            onLoad={() => setIconLoaded(true)}
            onError={() => setIconLoaded(true)}
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              flexShrink: 0,
              opacity: iconLoaded ? 1 : 0.7,
              transition: 'opacity 0.2s ease-in-out'
            }}
          />
          <Typography variant="h6" sx={{ color: '#eceef1', fontSize: 24, fontWeight: 'bold' }}>
            FIRESTONE CAD
          </Typography>
          <Typography variant="h6" sx={{ color: '#b4b9c0', fontSize: 24, fontWeight: 'bold' }}>
            |
          </Typography>
          
          {/* Status buttons moved to left */}
          <Box sx={{ display: 'flex', gap: 1, marginLeft: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => handleDispatchStatusChange('I/S')}
              sx={{
                backgroundColor: `${getStatusColor('I/S')} !important`,
                color: '#0a0c0e',
                minWidth: 60,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                border: dispatchStatus === 'I/S' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#2aa54a !important' }
              }}
            >
              I/S
            </Button>
            <Button
              variant="contained"
              onClick={() => handleDispatchStatusChange('OOS')}
              sx={{
                backgroundColor: `${getStatusColor('OOS')} !important`,
                color: '#0a0c0e',
                minWidth: 60,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                border: dispatchStatus === 'OOS' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#909090 !important' }
              }}
            >
              OOS
            </Button>
          </Box>
        </Box>

        {/* Right side: Time and Connection Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'bold' }}>
            {formatTime(currentTime)}
          </Typography>
          {isFetching && (
            <CircularProgress size={16} sx={{ color: '#3c8cff' }} />
          )}
          
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            backgroundColor: '#32be5a' // I/S color for connection status
          }} />
          <IconButton onClick={onShowRadio} sx={{ color: '#eceef1', padding: 0.5 }} title="Radio System">
            <RadioIcon fontSize="small" />
          </IconButton>
          {hasPermission('admin') && (
            <IconButton onClick={onShowAdminDialog} sx={{ color: '#eceef1', padding: 0.5 }} title="Admin Panel">
              <AdminIcon fontSize="small" />
            </IconButton>
          )}
          {hasPermission('shift-commander') && (
            <IconButton onClick={onShowShiftCommander} sx={{ color: '#eceef1', padding: 0.5 }} title="Shift Commander">
              <PeopleIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton onClick={onShowClosedCalls} sx={{ color: '#eceef1', padding: 0.5 }} title="View Closed Calls">
            <HistoryIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onShowAccountDialog} sx={{ color: '#eceef1', padding: 0.5 }} title="Account Settings">
            <AccountIcon fontSize="small" />
          </IconButton>
          {onReturnToShiftSelection && (
            <IconButton onClick={onReturnToShiftSelection} sx={{ color: '#eceef1', padding: 0.5 }} title="Return to Shift Selection">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton onClick={onLogout} sx={{ color: '#eceef1', padding: 0.5 }} title="Logout">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default DispatcherHeader;